import type { JSX } from 'react';
import { useEngine } from '../../state/engineStore';
import { fmtDuration } from '../shell/format';

function Stat({
  label,
  value,
  sub,
  bad,
}: {
  label: string;
  value: string;
  sub?: string;
  bad?: boolean;
}): JSX.Element {
  return (
    <div style={{ background: 'var(--panel)', border: '1px solid var(--line-dim)', borderRadius: 4, padding: '9px 11px' }}>
      <div className="muted" style={{ fontSize: 11 }}>
        {label}
      </div>
      <div className="num" style={{ fontSize: 19, color: bad ? 'var(--pri-high)' : 'var(--text)' }}>
        {value}
      </div>
      {sub && (
        <div className="muted" style={{ fontSize: 10.5 }}>
          {sub}
        </div>
      )}
    </div>
  );
}

export function KpiPanel(): JSX.Element {
  const kpi = useEngine((s) => s.snap?.kpi);
  const alarms = useEngine((s) => s.snap?.alarmMetrics);
  if (!kpi) return <div style={{ padding: 14 }}>Waiting for the engine…</div>;

  return (
    <div className="scroll" style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 14 }}>
      <section>
        <h3 style={{ margin: '0 0 8px', fontSize: 13 }}>Safety</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 8 }}>
          <Stat label="Trips" value={String(kpi.trips)} bad={kpi.trips > 0} />
          <Stat label="Relief lifts" value={String(kpi.reliefLifts)} bad={kpi.reliefLifts > 0} />
          <Stat label="Overflows" value={String(kpi.overflows)} bad={kpi.overflows > 0} />
          <Stat
            label="Time in the danger zone"
            value={`${kpi.dangerMinutes.toFixed(1)} min`}
            bad={kpi.dangerMinutes > 0}
            sub={kpi.dangerActive.join(' · ') || undefined}
          />
        </div>
      </section>

      <section>
        <h3 style={{ margin: '0 0 8px', fontSize: 13 }}>Product quality</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: 8 }}>
          {kpi.spec.map((s) => (
            <div key={s.tag} style={{ background: 'var(--panel)', border: '1px solid var(--line-dim)', borderRadius: 4, padding: '8px 11px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11.5 }}>
                <span className="muted">{s.label}</span>
                <span className="num">{s.value.toFixed(3)}</span>
              </div>
              <div style={{ height: 5, background: 'var(--bg-deep)', borderRadius: 3, marginTop: 6, overflow: 'hidden' }}>
                <div
                  style={{
                    width: `${s.onSpecFraction * 100}%`,
                    height: '100%',
                    background: s.onSpecFraction > 0.99 ? 'var(--ok)' : s.onSpecFraction > 0.95 ? 'var(--pri-medium)' : 'var(--pri-high)',
                  }}
                />
              </div>
              <div className="muted num" style={{ fontSize: 10.5, marginTop: 3 }}>
                {(s.onSpecFraction * 100).toFixed(2)} % on spec
              </div>
            </div>
          ))}
        </div>
      </section>

      <section>
        <h3 style={{ margin: '0 0 8px', fontSize: 13 }}>Production and consumption</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 8 }}>
          <Stat label={kpi.throughputLabel} value={`${kpi.throughput.toFixed(0)} ${kpi.throughputUnits}`} sub={`over ${fmtDuration(kpi.elapsedS)}`} />
          {kpi.energy.map((e) => (
            <Stat key={e.label} label={e.label} value={`${e.value.toFixed(1)} ${e.units}`} />
          ))}
          {kpi.chemicals.map((c) => (
            <Stat key={c.label} label={c.label} value={`${c.value.toFixed(1)} ${c.units}`} />
          ))}
        </div>
      </section>

      <section>
        <h3 style={{ margin: '0 0 8px', fontSize: 13 }}>Alarm hygiene</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 8 }}>
          <Stat
            label="Alarms per 10 min"
            value={String(alarms?.per10min ?? 0)}
            sub="EEMUA 191 target ≤ 1"
            bad={(alarms?.per10min ?? 0) > 1}
          />
          <Stat label="Standing alarms" value={String(alarms?.standing ?? 0)} bad={(alarms?.standing ?? 0) > 2} />
          <Stat label="Unacknowledged minutes" value={(alarms?.unackedMinutes ?? 0).toFixed(1)} />
          <Stat label="Flood minutes" value={(alarms?.floodMinutes ?? 0).toFixed(1)} bad={(alarms?.floodMinutes ?? 0) > 0} />
          <Stat label="Operator actions per hour" value={kpi.actionsPerHour.toFixed(1)} />
        </div>
      </section>

      <section>
        <h3 style={{ margin: '0 0 8px', fontSize: 13 }}>Loop performance</h3>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ textAlign: 'left', color: 'var(--text-mute)' }}>
              <th style={{ padding: '4px 8px' }}>Loop</th>
              <th style={{ padding: '4px 8px', textAlign: 'right' }}>IAE</th>
              <th style={{ padding: '4px 8px', textAlign: 'right' }}>ISE</th>
              <th style={{ padding: '4px 8px', textAlign: 'right' }}>Oscillation /h</th>
              <th style={{ padding: '4px 8px', textAlign: 'right' }}>Valve travel %</th>
              <th style={{ padding: '4px 8px', textAlign: 'right' }}>In auto</th>
              <th style={{ padding: '4px 8px', textAlign: 'right' }}>Saturated</th>
            </tr>
          </thead>
          <tbody>
            {kpi.loops.map((l) => (
              <tr key={l.id} style={{ borderTop: '1px solid var(--panel-2)' }}>
                <td className="num" style={{ padding: '3px 8px' }}>
                  {l.id}
                </td>
                <td className="num" style={{ padding: '3px 8px', textAlign: 'right' }}>
                  {l.iae.toFixed(0)}
                </td>
                <td className="num" style={{ padding: '3px 8px', textAlign: 'right' }}>
                  {l.ise.toFixed(0)}
                </td>
                <td className="num" style={{ padding: '3px 8px', textAlign: 'right', color: l.oscillation > 60 ? 'var(--pri-medium)' : undefined }}>
                  {l.oscillation.toFixed(0)}
                </td>
                <td className="num" style={{ padding: '3px 8px', textAlign: 'right' }}>
                  {l.travel.toFixed(0)}
                </td>
                <td className="num" style={{ padding: '3px 8px', textAlign: 'right' }}>
                  {(l.autoFraction * 100).toFixed(0)}%
                </td>
                <td className="num" style={{ padding: '3px 8px', textAlign: 'right' }}>
                  {(l.saturatedFraction * 100).toFixed(0)}%
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}
